import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { cloneDeep, isEmpty, omit } from 'lodash'
import { MouseEvent, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useMutation } from 'react-query'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'

import Button from '@/components/Button'
import Loading from '@/components/Card/Loading'
import Modal from '@/components/Modal'
import * as actions from '@/pages/SpaceAppBuilder/FeaturesPage/actions'
import { getAppBuilderFeatureData } from '@/pages/SpaceAppBuilder/FeaturesPage/selectors'
import { Feature, RelatedFeature } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { featureQueryOptions } from '@/pages/SpaceAppBuilder/utilities/queries'
import { useAppDispatch, useAppSelector } from '@/store/hooks'

import messages from './messages'

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(243, 243, 244, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
`
const Content = styled.div`
  padding: 30px 30px 0 30px;
  margin-bottom: 3rem;
`
const Row = styled.div`
  display: flex;
`
const StyledRow = styled(Row)`
  margin-bottom: 1.1rem;
  margin-top: 0;
  align-items: center;
`
const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
`
const RelatedFeatureList = styled.ul`
  padding-left: 3em;
  margin: 0;
  color: #4b4b4b;
`
const RelatedFeatureToDelete = styled.li`
  list-style-type: disc;
  padding-bottom: 10px;
`
const StyleParagraph = styled.p`
  font-size: 1em;
  margin: 0;
`

interface StyledDeleteConfirmationWithInputProps {
  isOpen: boolean
  onClose: () => void
  afterConfirm?: () => void
  deleteFrom?: string
  relatedFeatures?: [] | Partial<RelatedFeature>[]
  agentsFlagOn?: boolean
}

const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  afterConfirm = () => {},
  deleteFrom,
  relatedFeatures = [],
  agentsFlagOn = false
}: StyledDeleteConfirmationWithInputProps) => {
  const intl = useIntl()
  const { spaceId, appId, featureId } = useParams<{
    spaceId: string
    appId: string
    featureId: string
  }>()
  const mutateFeature = useMutation({})
  const dispatch = useAppDispatch()
  const featureFetchState = useAppSelector(getAppBuilderFeatureData)

  const initialFocus = useRef(null)
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false)

  const closeModal = () => {
    afterConfirm()
    setIsDeleteInProgress(false)
    onClose()
  }

  const deleteRelatedFeatures = async () => {
    for (const relatedFeature of relatedFeatures) {
      try {
        // @ts-expect-error
        await mutateFeature.mutateAsync([
          featureQueryOptions({
            spaceId,
            appId,
            featureId: relatedFeature.featureId
          }).queryKey[0],
          { method: 'DELETE' }
        ])
      } catch {}
    }
  }

  const updateParentFeature = async () => {
    const patchFeature = omit(cloneDeep(featureFetchState.feature), [
      'id',
      'workflow',
      'createdBy',
      'modifiedBy',
      'createdDate',
      'modifiedDate',
      'audience'
    ]) as Feature

    // const relatedFeatures = patchFeature?.relatedFeatures || []
    const newRelatedFeatures: RelatedFeature[] =
      patchFeature.relatedFeatures?.filter(
        (feature: RelatedFeature) =>
          !relatedFeatures.some(
            (relatedFeature: Partial<RelatedFeature>) => relatedFeature.featureId === feature.featureId
          )
      ) || []
    await dispatch(
      actions.patchAppInstanceFeature({
        instanceId: appId,
        featureId,
        feature: {
          ...patchFeature,
          relatedFeatures: newRelatedFeatures
        }
      })
    )
  }

  const onConfirmDelete = async (event: MouseEvent) => {
    if (deleteFrom === 'canvas') {
      event.stopPropagation()
    } else {
      event.preventDefault()
    }
    // delete related feature
    if (!isEmpty(relatedFeatures)) {
      await updateParentFeature()
      await deleteRelatedFeatures()
    }

    setIsDeleteInProgress(true)
    closeModal()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" initialFocus={initialFocus}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div onClick={event => event.stopPropagation()}>
        {isDeleteInProgress && (
          <LoaderOverlay>
            <Loading
              isLoading={true}
              loadingMessage={
                deleteFrom === 'panel-action-set'
                  ? `Deleting Action ${agentsFlagOn ? 'Capability' : 'Feature'}`
                  : 'Deleting Step'
              }
            />
          </LoaderOverlay>
        )}
        <Content>
          <StyledRow>
            <div className="mx-auto flex h-12 w-16 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
            </div>
            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
              <div className="text-base font-semibold leading-6 text-gray-900">
                {deleteFrom === 'panel-action-set'
                  ? `Are you sure you want to delete this action ${agentsFlagOn ? 'capability' : 'feature'}?`
                  : deleteFrom === 'trigger-canvas'
                    ? 'Are you sure you want to change the trigger?'
                    : deleteFrom === 'trigger-panel'
                      ? 'Are you sure you want to delete the trigger?'
                      : 'Are you sure you want to delete this step?'}
              </div>
            </div>
          </StyledRow>
          {!isEmpty(relatedFeatures) && (
            <>
              <StyledRow>
                <StyleParagraph>{intl.formatMessage(messages.deleteConfirmationSubMessage)}</StyleParagraph>
              </StyledRow>
              <StyledRow>
                <StyleParagraph>
                  {`You are about to delete the following related ${
                    relatedFeatures.length > 1
                      ? agentsFlagOn
                        ? 'capabilities'
                        : 'features'
                      : agentsFlagOn
                        ? 'capability'
                        : 'feature'
                  }:`}
                </StyleParagraph>
              </StyledRow>
              <StyledRow>
                <RelatedFeatureList>
                  {relatedFeatures?.map((feature: Partial<RelatedFeature>) => (
                    <RelatedFeatureToDelete key={feature.featureId}>{feature.name}</RelatedFeatureToDelete>
                  ))}
                </RelatedFeatureList>
              </StyledRow>
            </>
          )}
        </Content>

        <ButtonRow>
          <Button
            ref={initialFocus}
            onClick={event => {
              event.stopPropagation()
              onClose()
            }}
            color="secondary"
          >
            {intl.formatMessage(messages.cancel)}
          </Button>
          <Button
            onClick={event => {
              event.stopPropagation()
              onConfirmDelete(event)
            }}
            color={deleteFrom === 'trigger-canvas' ? 'primary' : 'warning'}
          >
            {deleteFrom === 'trigger-canvas'
              ? intl.formatMessage(messages.deleteConfirmationModalChangeButton)
              : intl.formatMessage(messages.deleteConfirmationModalDeleteButton)}
          </Button>
        </ButtonRow>
      </div>
    </Modal>
  )
}

export default DeleteConfirmationModal
